import logger from '../../../utils/logger';
/**
 * Updated this legacy service to be the singular one for uploads, and xls download generation.
 **/

// ExcelJS is dynamically imported when needed to reduce initial bundle size
import { getTownViewXLSKeys, COLUMN_CONFIG, INTERNAL_XLS_KEYS } from '../SummaryCaseTable/config/columns';
import { usePersistedDataStore } from '../../store';
import { getConversionFactor } from '../../../utils/conversionUtils';
import { getMostRecentOfferRound, getMostRecentOfferValue } from '../../../utils/mostRecentOfferUtils';
import { isCaseSettled, wereAnyOffersSent, isWorkupAboveIfmv } from '../SummaryCaseTable/utils/helpers';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import { getIFMVUtil, getAVUtil } from '../../../utils/getAVUtil';
import { findWorkupFile, findWorkupFileByWorkupReportId } from '../Workups/WorkupVisualization/workupFileHelpers.js';
import { useOfferOptionsStore } from './offerOptionsStore.js';
import { normalizeCompId } from '../common/compSheetLookup.js';
import { calculateProprietyWorkup } from '../SummaryCaseTable/services/summaryRowCreationService.js';
import { savedCompIds, fetchCompAddressById } from '../common/compAddressLookup.js';

const getLatestRoundInfo = (negotiations) => {
  const townRound = getMostRecentOfferRound(negotiations, 'Town');
  const liveAventineRound = getMostRecentOfferRound(negotiations, 'Aventine', true);
  const latestRound = Math.max(townRound ?? 0, liveAventineRound ?? 0);
  const priorRound = latestRound > 0 ? latestRound - 1 : null;
  return { townRound, liveAventineRound, latestRound, priorRound };
};

// Had to rewrite the below to enable frontend uploads to get around file size limit
const contentTypeFromExtension = (filename, blobType) => {
  const ext = filename.split('.').pop().toLowerCase();
  const map = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json'
  };
  return map[ext] || blobType || 'application/octet-stream';
};

/**
 * Get presigned PUT URL from backend, then upload blob directly to S3 and confirm. Avoids backend filesize limits.
 * @param {Blob} fileBlob - The file blob to upload
 * @param {string} s3Key - S3 object key (e.g. Workups/workup_123.pdf)
 * @param {string} bucketName - Bucket name (required; use 'negotiation-files' for workups)
 * @param {string} downloadFilename - Optional custom filename for downloads
 * @returns {Promise<string>} - The S3 download URL
 */
export const uploadFileToS3Presigned = async (
  fileBlob,
  s3Key,
  bucketName = 'negotiation-files',
  downloadFilename = null
) => {
  const contentType = contentTypeFromExtension(s3Key, fileBlob.type);
  const { data: urlData } = await axiosInstance.post('/presigned-upload-url', {
    s3_key: s3Key,
    content_type: contentType,
    bucket_name: bucketName
  });
  if (!urlData.success || !urlData.presigned_url) throw new Error(urlData.error || 'Failed to get presigned URL');
  const putRes = await fetch(urlData.presigned_url, {
    method: 'PUT',
    body: fileBlob,
    headers: { 'Content-Type': contentType }
  });
  if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status} ${putRes.statusText}`);
  const { data: confirmData } = await axiosInstance.post('/confirm-s3-upload', {
    s3_key: s3Key,
    bucket_name: bucketName,
    download_filename: downloadFilename || undefined
  });
  if (!confirmData.success) throw new Error(confirmData.error || 'Failed to confirm upload');
  return confirmData.s3_url;
};

/**
 * Uploads a file to S3 via the backend API
 * @param {Blob} fileBlob - The file blob to upload
 * @param {string} filename - The filename for the S3 object
 * @param {string} contentType - The MIME type of the file
 * @param {string} bucketName - Optional bucket name (uses default if not provided)
 * @param {string} downloadFilename - Optional custom filename for downloads (sets Content-Disposition header)
 * @returns {Promise<string>} - The S3 URL of the uploaded file
 */
export const uploadFileToS3 = async (
  fileBlob,
  filename,
  contentType,
  bucketName = null,
  downloadFilename = null
) => {
  try {
    const arrayBuffer = await fileBlob.arrayBuffer();
    const formData = new FormData();
    const file = new File([arrayBuffer], filename, { type: contentType });
    formData.append('file', file);
    formData.append('content_type', contentType);
    if (bucketName) formData.append('bucket_name', bucketName);
    if (downloadFilename) formData.append('download_filename', downloadFilename);

    const response = await axiosInstance.post('/upload-file-s3', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (response.data.success) return response.data.s3_url;
    throw new Error(response.data.error || 'Upload failed');
  } catch (error) {
    logger.error('S3 upload error:', error);
    throw new Error(`Failed to upload file to S3: ${error.message}`);
  }
};

/**
 * Uploads a file to S3 with automatic content type detection
 * @param {Blob} fileBlob - The file blob to upload
 * @param {string} filename - The filename for the S3 object
 * @param {string} bucketName - Optional bucket name
 * @param {string} downloadFilename - Optional custom filename for downloads (sets Content-Disposition header)
 * @returns {Promise<string>} - The S3 URL of the uploaded file
 */
export const uploadFileToS3Auto = async (fileBlob, filename, bucketName = null, downloadFilename = null) => {
  const contentType = contentTypeFromExtension(filename, fileBlob.type);
  return uploadFileToS3(fileBlob, filename, contentType, bucketName, downloadFilename);
};

export const uploadExcelToS3 = async (excelBlob, filename, bucketName = null) => {
  return uploadFileToS3Auto(excelBlob, filename, bucketName);
};

export const uploadPDFToS3 = async (pdfBlob, filename, bucketName = null) => {
  return uploadFileToS3Auto(pdfBlob, filename, bucketName);
};

const workupHyperlinkText = (url, rowData) => {
  const s = String(url || '').trim();
  if (!s) return '';
  const address = String(rowData?.Address || rowData?.address || rowData?.parcel_id || '').trim();
  return address ? `${address} comps.pdf` : 'comps.pdf';
};

const taxYearToExcelNumber = (raw) => {
  if (raw === undefined || raw === null || raw === '') return '';
  const n = Number(raw);
  return Number.isFinite(n) ? n : '';
};

/** 5-digit US ZIP as Excel number (ZIP+4 uses first five digits); empty when not parseable. */
const zipToExcelNumber = (raw) => {
  if (raw === undefined || raw === null || raw === '') return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{5})(?:-\d{4})?$/);
  if (m) return parseInt(m[1], 10);
  const digits = s.replace(/\D/g, '');
  if (digits.length < 5) return '';
  const n = parseInt(digits.slice(0, 5), 10);
  return Number.isFinite(n) ? n : '';
};

/**
 * Get current round comment for party (mirrors DownloadButton logic)
 */
export const getCurrentRoundCommentForParty = (negotiations, party) => {
  if (!negotiations || !Array.isArray(negotiations)) {
    return '';
  }

  // First, find the highest round number that has any offers
  let currentRound = 0;
  for (const round of negotiations) {
    if (round.round > currentRound && round.offers && round.offers.length > 0) {
      currentRound = round.round;
    }
  }

  // If no rounds found, return empty
  if (currentRound === 0) {
    return '';
  }

  // Get the current round data
  const currentRoundData = negotiations.find(round => round.round === currentRound);
  if (!currentRoundData || !currentRoundData.offers) {
    return '';
  }

  // Find the offer from the specified party in the current round
  const partyOffer = currentRoundData.offers.find(offer => offer.OfferedBy === party);
  if (partyOffer && partyOffer.Comments && partyOffer.Comments.trim() !== '') {
    return partyOffer.Comments.trim();
  }

  return '';
};

const townCommentOnRound = (negotiations, roundNumber) => {
  if (!Array.isArray(negotiations)) return '';
  const round = negotiations.find((r) => Number(r?.round) === roundNumber);
  const comment = round?.offers?.find((o) => o?.OfferedBy === 'Town')?.Comments;
  return typeof comment === 'string' ? comment.trim() : '';
};

/** Keys for rounds that have a town comment on at least one exported row. */
const townRoundCommentColumnKeys = (rows) => {
  const rounds = new Set();
  (rows || []).forEach((item) => {
    if (!Array.isArray(item?.Negotiations)) return;
    item.Negotiations.forEach((round) => {
      const n = Number(round?.round);
      if (!Number.isFinite(n) || n <= 0) return;
      const comment = round?.offers?.find((o) => o?.OfferedBy === 'Town')?.Comments;
      if (typeof comment === 'string' && comment.trim()) rounds.add(n);
    });
  });
  return [...rounds].sort((a, b) => a - b).map((n) => `TownRound${n}Comment`);
};

/**
 * Generate round columns from negotiations data (mirrors DownloadButton logic)
 */
const generateRoundColumns = (negotiations, avToggleActive = false, rowData = null, townView = false, municipalityName = 'Town') => {
  if (!negotiations || !Array.isArray(negotiations) || negotiations.length === 0) {
    return [];
  }

  // Sort negotiations by round number
  const sortedNegotiations = [...negotiations].sort((a, b) => (a.round || 0) - (b.round || 0));
  
  const roundColumns = [];
  
  sortedNegotiations.forEach(negotiation => {
    if (negotiation.round && negotiation.offers && Array.isArray(negotiation.offers)) {
      const roundNumber = negotiation.round;
      const aventineOffer = negotiation.offers.find(offer => offer.OfferedBy === 'Aventine');
      const townOffer = negotiation.offers.find(offer => offer.OfferedBy === 'Town');
      
      // Format the round column value (without "Round #" - that's in the header)
      let roundValue = '';
      let hasAventineOffer = false;
      // let hasTownOffer = false;
      
      if (aventineOffer && aventineOffer.Offer !== null && aventineOffer.Offer !== undefined) {
        let aventineDisplayValue = aventineOffer.Offer;
        if (!avToggleActive && rowData) {
          const conversionFactor = getConversionFactor(rowData.EQ, rowData.RAR);
          aventineDisplayValue = conversionFactor > 0 ? Number(aventineOffer.Offer) / conversionFactor : Number(aventineOffer.Offer);
        }
        roundValue += avToggleActive ? `Ave: ${Math.round(aventineDisplayValue).toLocaleString()} AV` : `Ave: $${Math.round(aventineDisplayValue).toLocaleString()}`;
        hasAventineOffer = true;
      }
      
      if (townOffer && townOffer.Offer !== null && townOffer.Offer !== undefined) {
        let townDisplayValue = townOffer.Offer;
        if (!avToggleActive && rowData) {
          const conversionFactor = getConversionFactor(rowData.EQ, rowData.RAR);
          townDisplayValue = conversionFactor > 0 ? Number(townOffer.Offer) / conversionFactor : Number(townOffer.Offer);
        }
        if (hasAventineOffer) {
          roundValue += avToggleActive ? ` | ${municipalityName}: ${Math.round(townDisplayValue).toLocaleString()} AV` : ` | ${municipalityName}: $${Math.round(townDisplayValue).toLocaleString()}`;
        } else {
          roundValue += avToggleActive ? `${municipalityName}: ${Math.round(townDisplayValue).toLocaleString()} AV` : `${municipalityName}: $${Math.round(townDisplayValue).toLocaleString()}`;
        }
        // hasTownOffer = true;
      }
      
      roundColumns.push({
        key: `Round${roundNumber}`,
        value: roundValue,
        roundNumber: roundNumber
      });

      // Add comment column for non-town view
      if (!townView) {
        let commentValue = '';
        let hasAventineComment = false;
        // let hasTownComment = false;
        
        if (aventineOffer && aventineOffer.Comments && aventineOffer.Comments.trim() !== '') {
          commentValue += `Ave: ${aventineOffer.Comments}`;
          hasAventineComment = true;
        }
        
        if (townOffer && townOffer.Comments && townOffer.Comments.trim() !== '') {
          if (hasAventineComment) {
            commentValue += ` | ${municipalityName}: ${townOffer.Comments}`;
          } else {
            commentValue += `${municipalityName}: ${townOffer.Comments}`;
          }
          // hasTownComment = true;
        }
        
        roundColumns.push({
          key: `Round${roundNumber}Comment`,
          value: commentValue,
          roundNumber: roundNumber
        });
      }
    }
  });
  
  return roundColumns;
};

/** Town export visibility: live offer (or settled); pass checkWorkupEq for workup EQ cap. */
const missingLiveOfferForTownExport = (item) =>
  !wereAnyOffersSent(item?.Negotiations) && !isCaseSettled(item);

const townSensitiveExportVisible = (item, { checkWorkupEq = false, requireLiveOffer = true } = {}) => {
  if (requireLiveOffer && missingLiveOfferForTownExport(item)) return false;
  if (!checkWorkupEq) return true;
  const raw = item.ProprietyWorkup ?? item.SubjectMarketValue;
  const wu = parseFloat(raw);
  if (!Number.isFinite(wu) || wu <= 0) return false;
  return !isWorkupAboveIfmv(item);
};

/** Comps JSON when the row still has it; otherwise Comp1–Comp5 ids on the unpacked grid row. */
const rowCompIds = (item) => {
  const fromComps = savedCompIds(item?.Comps);
  if (fromComps.length) return fromComps;
  return [1, 2, 3, 4, 5].map((n) => {
    const id = normalizeCompId(item?.[`Comp${n}`]);
    return id && !/\s/.test(id) ? id : '';
  });
};

/**
 * Process fields consistently (shared by generateXLSData and optional callers)
 */
export const processXlsFields = (
  item,
  exportKeys,
  fieldMappers,
  pidToAddressMap,
  townView = false,
  resolveCompAddresses = true,
  requireLiveOffer = true,
) => {
  const processedItem = {};
  const compIds = rowCompIds(item);

  // Process each key using the field mappers
  for (const key of exportKeys) {
    if (fieldMappers[key]) {
      processedItem[key] = fieldMappers[key]();
    } else if (key.match(/^Comp[1-5]$/)) {
      if (townView && !townSensitiveExportVisible(item, { requireLiveOffer })) {
        processedItem[key] = '';
        continue;
      }
      const slot = parseInt(key.replace('Comp', ''), 10);
      const compId = compIds[slot - 1] || '';
      const address = compId ? (pidToAddressMap[compId] || '') : '';
      processedItem[key] = resolveCompAddresses ? (address || (compId ? '' : (item[key] || ''))) : (compId || item[key] || '');
      
    } else {
      // Default field mapping
      processedItem[key] = item[key] !== undefined ? item[key] : '';
    }
  }
  
  return processedItem;
};

/**
 * Generate XLS workbook blob (single code path for S3 offers and browser download).
 * @param {object[]} caseData - Rows to export when selectedRows is not used as export set (downloads pass full filtered set here).
 * @param {object[]|number[]|null} selectedRows - Offer flow: subset row objects to export. Otherwise omit/null; comp-index arrays are not used here (use gridRows).
 * @param {string[]|null} keys - Column keys; when null, derived from townView + includeCompsInOffersSheet.
 * Negotiation context always comes from usePersistedDataStore (negotiationObj).
 * @param {object} [genOptions]
 * @param {object[]} [genOptions.rowsToExport] - Explicit rows (overrides selectedRows/caseData resolution).
 * @param {object[]} [genOptions.gridRows] - Full grid rows for compSheet index (_rowNumber or parcel_id match).
 * @param {(pid: string) => string} [genOptions.getMunicipalityFromParcelId]
 * @param {boolean} [genOptions.includeCompsInOffersSheet]
 * @param {boolean} [genOptions.isRestricted] - Town worksheet protection; default true (S3). Pass false for unlocked internal download.
 * @param {string} [genOptions.workupReportTypeForXls] - WORKUP_REPORT_TYPES id; Workup column uses {@link findWorkupFileByWorkupReportId} when set.
 * @param {object} [genOptions.user] - When town view and `keys` omitted, passed to town XLS key list (link `shareWorkups` / `ShowGridComps`).
 * @param {boolean} [genOptions.blankUnsettledTownFields] - External download: leave town response, counter, and notes empty unless the case is settled.
 * @param {boolean} [genOptions.includeTownRoundNotes] - Town-view download only: add Town Note N for rounds that have a town comment.
 */
export const generateXLSData = async (
  caseData,
  selectedRows,
  keys,
  townView = true,
  villageMode = false,
  avToggleActive = false,
  caseType,
  genOptions = {}
) => {
  logger.log('generateXLSData called with options:', caseData);
  const {
    rowsToExport,
    gridRows,
    getMunicipalityFromParcelId: muniLookup,
    includeCompsInOffersSheet = true,
    isRestricted = true,
    workupReportTypeForXls = null,
    user,
    requireLiveOffer = true,
    throwOnMissingLiveOffer = false,
    blankUnsettledTownFields = false,
    includeTownRoundNotes = false,
  } = genOptions;

  const store = usePersistedDataStore.getState();
  const { compSheet, negotiationObj, rar, eq } = store;

  logger.log('🔄 [S3 UPLOAD SERVICE] Generating XLS data with ExcelJS:', {
    caseDataLength: caseData?.length,
    selectedRowsLength: selectedRows?.length,
    keysLength: keys?.length,
    townView,
    villageMode,
    avToggleActive,
    caseType,
    reportType: negotiationObj?.reportType,
  });

  const selectedAreRowObjects =
    Array.isArray(selectedRows) &&
    selectedRows.length > 0 &&
    typeof selectedRows[0] === 'object' &&
    selectedRows[0] !== null;

  let dataToProcess =
    Array.isArray(rowsToExport) && rowsToExport.length > 0
      ? rowsToExport
      : selectedAreRowObjects
        ? selectedRows
        : Array.isArray(caseData)
          ? caseData
          : [];

  logger.log('data to process', dataToProcess);

  if (!dataToProcess || dataToProcess.length === 0) {
    throw new Error('No data available to process');
  }

  const exportKeys =
    keys?.length
      ? keys
      : townView
        ? getTownViewXLSKeys({
            user,
            includeCompsInOffersSheet,
            workupOptions: genOptions.workupOptions,
          })
        : [...INTERNAL_XLS_KEYS];

  const columnConfig = townView ? COLUMN_CONFIG.townView : COLUMN_CONFIG.internal;

  // Check if any rows have portfolio offers - if not, remove portfolio fields from export
  const hasPortfolioOffers = dataToProcess.some(item => 
    item.PortfolioOffer !== null && 
    item.PortfolioOffer !== 'None' && 
    item.PortfolioOffer !== '' && 
    !isNaN(Number(item.PortfolioOffer))
  );
  
  let finalExportKeys = exportKeys;
  if (!hasPortfolioOffers) {
    // Remove portfolio fields from export keys if no portfolio offers exist
    finalExportKeys = exportKeys.filter(key => 
      key !== 'PortfolioOffer' && key !== 'PortfolioRequestedAV'
    );
  }

  // Refresh S3Files for workup column if we include the individual workup links
  if (townView && finalExportKeys.includes('Workup')) {
    try {
      const taxYearRaw = negotiationObj?.TaxYear ?? negotiationObj?.taxYear;
      const taxYear = Number.parseInt(String(taxYearRaw), 10);
      if (Number.isFinite(taxYear)) {
        const parcelIds = Array.from(
          new Set(
            dataToProcess
              .map((r) => r?.parcel_id ?? r?.ParcelID)
              .filter(Boolean)
              .map((p) => String(p))
          )
        );

        if (parcelIds.length > 0) {
          const { data: resp } = await axiosInstance.post('/get_s3_files_batch', {
            parcel_ids: parcelIds,
            tax_year: taxYear,
            bucket_name: 'aventine-court-docs',
          });

          const s3FilesMap = resp?.success ? resp?.s3_files : null;
          if (s3FilesMap && typeof s3FilesMap === 'object') {
            dataToProcess = dataToProcess.map((row) => {
              const pid = row?.parcel_id ?? row?.ParcelID;
              const key = pid == null ? null : String(pid);
              const freshFiles = key ? s3FilesMap[key] : null;
              return Array.isArray(freshFiles) ? { ...row, S3Files: freshFiles } : row;
            });
          }
        }
      }
    } catch (e) {
      logger.warn('⚠️ [S3 UPLOAD SERVICE] Failed to refresh S3Files; continuing with existing URLs.', e);
    }
  }

  // Offer/comment round columns (Round1, Round1Comment, …) are internal-only.
  // Town Note N is added only for the town-view download button.
  const itemRoundColumnsMap = new Map();
  let roundColumnKeys = [];

  if (!townView) {
    const allRoundColumns = new Set();
    dataToProcess.forEach((item, index) => {
      if (!item.Negotiations || !Array.isArray(item.Negotiations)) return;
      const municipalityName =
        muniLookup?.(item.parcel_id) || negotiationObj?.muniString || item.Town || 'Town';
      const roundColumns = generateRoundColumns(
        item.Negotiations,
        avToggleActive,
        item,
        townView,
        municipalityName
      );
      itemRoundColumnsMap.set(index, roundColumns);
      roundColumns.forEach((round) => allRoundColumns.add(round.key));
    });

    roundColumnKeys = Array.from(allRoundColumns).sort((a, b) => {
      const aRound = parseInt(a.replace('Round', '').replace('Comment', ''), 10);
      const bRound = parseInt(b.replace('Round', '').replace('Comment', ''), 10);
      if (aRound !== bRound) return aRound - bRound;
      return a.includes('Comment') ? 1 : -1;
    });

    if (roundColumnKeys.length > 0) {
      const anchorIndex = finalExportKeys.indexOf('InternalNote');
      if (anchorIndex !== -1) {
        finalExportKeys.splice(anchorIndex + 1, 0, ...roundColumnKeys);
      } else {
        finalExportKeys.push(...roundColumnKeys);
      }
    }
  }

  if (townView) {
    const commentKeys = includeTownRoundNotes
      ? townRoundCommentColumnKeys(dataToProcess).filter((k) => !finalExportKeys.includes(k))
      : [];
    if (commentKeys.length) {
      const notesIndex = finalExportKeys.indexOf('TownNotes');
      finalExportKeys.splice(notesIndex === -1 ? finalExportKeys.length : notesIndex, 0, ...commentKeys);
    }

    const townNotesIndex = finalExportKeys.indexOf('TownNotes');
    const insertAt =
      roundColumnKeys.length > 0 && townNotesIndex !== -1
        ? townNotesIndex + 1 + roundColumnKeys.length
        : townNotesIndex !== -1
          ? townNotesIndex + 1
          : finalExportKeys.length;
    if (!finalExportKeys.includes('PriorTownOffer')) {
      finalExportKeys.splice(insertAt, 0, 'PriorTownOffer');
    }
  }

  const resolveCompSheetIndex = (item, rowIdx) => {
    if (item._rowNumber != null && Number.isFinite(Number(item._rowNumber))) {
      return Math.max(0, Number(item._rowNumber) - 1);
    }
    if (Array.isArray(gridRows) && gridRows.length) {
      const i = gridRows.findIndex((r) => r && (r.parcel_id === item.parcel_id || r.id === item.id));
      if (i >= 0) return i;
    }
    return rowIdx;
  };

  const exportHasCompKeys = finalExportKeys.some((k) => /^Comp[1-5]$/.test(k));
  const resolveCompAddresses =
    exportHasCompKeys &&
    (!townView ||
      (user?.isLinkAuthenticated ? Boolean(user?.ShowGridComps) : includeCompsInOffersSheet !== false));

  const addressRows = resolveCompAddresses
    ? dataToProcess.filter((item) => !townView || townSensitiveExportVisible(item, { requireLiveOffer }))
    : [];
  const reportTypeForComps = negotiationObj?.reportType || store.reportType;
  const requestedCompIds = [...new Set(addressRows.flatMap((item) => rowCompIds(item).filter(Boolean)))];
  const pidToAddressMap = resolveCompAddresses
    ? {
        ...(negotiationObj?.compAddressByPid || {}),
        ...(await fetchCompAddressById({
          ids: requestedCompIds,
          reportType: reportTypeForComps,
        })),
      }
    : {};

  const firstRow = dataToProcess[0];
  logger.log('[XLS export] raw input before key mapping', {
    rowCount: dataToProcess.length,
    townView,
    finalExportKeys,
    firstRowKeysSorted: firstRow ? Object.keys(firstRow).sort() : [],
    firstRowRaw: firstRow,
  });

  if (townView && requireLiveOffer && throwOnMissingLiveOffer) {
    const missingLive = dataToProcess.filter((item) => missingLiveOfferForTownExport(item));
    if (missingLive.length > 0) {
      const pids = missingLive.map((item) => item?.parcel_id || item?.id).filter(Boolean);
      logger.error('[XLS WORKUP] LiveOffer gate failed on offer send', { count: missingLive.length, pids });
      throw new Error(
        `LiveOffer gate: ${missingLive.length} unsettled case(s) have no live Aventine offer before town export (${pids.slice(0, 8).join(', ')}${pids.length > 8 ? ', …' : ''}).`
      );
    }
  }

  // Process data with field mapping (mirrors DownloadButton exactly)
  const reorderedData = await Promise.all(dataToProcess.map(async (item, index) => {
    const fullIndex = resolveCompSheetIndex(item, index);

    const compSheetItem = compSheet ? compSheet[fullIndex] : null;

    // --- Unified field selection for village (uses getNegotiationObj.villageRun) ---
    const ifmv = Number(getIFMVUtil(item));
    const avVal = getAVUtil(item);
    const propertyAssessment = avVal != null ? Number(avVal) : 0;

    // Calculate Propriety Workup directly if not available in item
    let proprietyWorkup = item.ProprietyWorkup;
    if (!proprietyWorkup && compSheetItem?.parcel_id) {
      try {
        const workup = calculateProprietyWorkup(item, compSheetItem);
        if (typeof workup === 'number' && workup > 0) proprietyWorkup = workup;
      } catch (e) {
        logger.error("Error calculating propriety workup:", e);
      }
    }

    // Field mapping configuration (mirrors DownloadButton exactly)
    const fieldMappers = {
      'Muni': () => muniLookup?.(item.parcel_id) || negotiationObj?.muniString || item.Town || '',
      'PropertyZip': () => zipToExcelNumber(item.ZipCode ?? item.PropertyZip),
      // Town sheet: column header stays "LOA"; cell value is RAR (row then negotiation store).
      'LOA': () => {
        if (!townView) return '';
        const v = item.RAR;
        if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
        if (rar != null && rar !== '' && Number.isFinite(Number(rar))) return Number(rar);
        return '';
      },
      'RAR': () => {
        const v = item.RAR;
        if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
        if (rar != null && rar !== '' && Number.isFinite(Number(rar))) return Number(rar);
        return '';
      },
      'EQ': () => {
        const v = item.EQ;
        if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
        if (eq != null && eq !== '' && Number.isFinite(Number(eq))) return Number(eq);
        return '';
      },
      'RequestedAV': () =>
        !townView
          ? item.RequestedAV !== undefined && item.RequestedAV !== null
            ? Math.round(Number(item.RequestedAV))
            : ''
          : item.OfferValue,
      'PortfolioOffer': () => {
        return item.PortfolioOffer !== null && item.PortfolioOffer !== 'None' && item.PortfolioOffer !== '' && !isNaN(Number(item.PortfolioOffer)) 
          ? Math.round(Number(item.PortfolioOffer))
          : '';
      },
      'PortfolioRequestedAV': () => {
        const portfolioRequestedValue = item.PortfolioRequestedAV;
        return portfolioRequestedValue !== undefined && portfolioRequestedValue !== null && portfolioRequestedValue !== 'None' && portfolioRequestedValue !== '' && !isNaN(Number(portfolioRequestedValue)) 
          ? Math.round(Number(portfolioRequestedValue))
          : '';
      },
      'IFMV': () => (!Number.isNaN(ifmv) ? Math.round(ifmv) : ''),
      'PropertyAssessment': () => propertyAssessment,
      'RecentSale': () =>
        item.RecentSale === false || item.RecentSale === 0 || item.RecentSale === '0'
          ? ''
          : item.RecentSale === true || item.RecentSale === 1 || item.RecentSale === '1'
            ? 'TRUE'
            : item.RecentSale ?? '',
      'SalePrice': () => item.RecentSale ? (item.SalePrice || '') : '',
      'SaleDate': () => item.RecentSale ? (item.SaleDate || '') : '',
      'SubjectMarketValue': () => {
        if (townView && !townSensitiveExportVisible(item, { checkWorkupEq: true, requireLiveOffer })) return '';
        const workupValue = item.ProprietyWorkup ?? item.SubjectMarketValue;
        return workupValue && !isNaN(parseFloat(workupValue)) && parseFloat(workupValue) !== 0
          ? Math.round(parseFloat(workupValue))
          : '';
      },
      'CaseStrength': () => {
        const caseStrength = item.CaseStrength;
        if (caseStrength !== null && caseStrength !== undefined && !isNaN(parseFloat(caseStrength))) {
          return parseFloat(caseStrength) / 100;
        }
        return '';
      },
      'Top5Average': () => {
        const top5Value = item.Top5Average;
        return top5Value && !isNaN(parseFloat(top5Value)) && parseFloat(top5Value) !== 0 
          ? Math.round(parseFloat(top5Value))
          : '';
      },
      'Top10WeightedAverage': () => {
        const weightedValue = item.Top10WeightedAverage;
        return weightedValue && !isNaN(parseFloat(weightedValue)) && parseFloat(weightedValue) !== 0 
          ? Math.round(parseFloat(weightedValue)) 
          : '';
      },
      'Top10ExponentialWeightedAverage': () => {
        const exponentialValue = item.Top10ExponentialWeightedAverage;
        return exponentialValue && !isNaN(parseFloat(exponentialValue)) && parseFloat(exponentialValue) !== 0 
          ? Math.round(parseFloat(exponentialValue))
          : '';
      },
      'Top10LinearWeightedAverage': () => {
        const linearValue = item.Top10LinearWeightedAverage;
        return linearValue && !isNaN(parseFloat(linearValue)) && parseFloat(linearValue) !== 0 
          ? Math.round(parseFloat(linearValue)) 
          : '';
      },
      'IsAdjustedOffer': () => {
        return item.IsAdjustedOffer ? 'Yes' : 'No';
      },
      'OfferValue': () => {
        // For settled cases, use settlement value; otherwise use offer value
        if (!item.isActive && item.SettlementValue && item.SettlementValue > 0) {
          // Convert AV settlement value to MV for spreadsheet display
          const conversionFactor = getConversionFactor(item.EQ, item.RAR);
          if (conversionFactor > 0) {
            return Math.round(item.SettlementValue / conversionFactor / 10) * 10;
          }
          return Math.round(item.SettlementValue);
        }
        
        // Convert AV to MV for spreadsheet display
        if (item.OfferValue !== undefined && item.OfferValue !== null && item.OfferValue !== 'None' && item.OfferValue !== '') {
          const offerValue = Number(item.OfferValue);
          if (!isNaN(offerValue) && offerValue > 0) {
            // Convert AV to MV using LOA
            const conversionFactor = getConversionFactor(item.EQ, item.RAR);
            if (conversionFactor > 0) {
              return Math.round(offerValue / conversionFactor / 10) * 10;
            }
            return Math.round(offerValue);
          }
        }
        return '';
      },
      'MarketValue': () => {
        const marketValue = item.MarketValue || item.SubjectMarketValue;
        return marketValue && !isNaN(parseFloat(marketValue)) && parseFloat(marketValue) !== 0 
          ? Math.round(parseFloat(marketValue)) 
          : '';
      },
      'TownResponse': () => {
        if (!townView) return item.TownResponse || '';

        const settled = isCaseSettled(item);
        if (settled) {
          return 'Accept';
        }
        if (blankUnsettledTownFields) return '';

        const negotiations = item?.Negotiations;
        const { townRound, latestRound } = getLatestRoundInfo(negotiations);
        if (townRound != null && townRound === latestRound) return 'Counter';

        return item.TownResponse || '';
      },
      'TownCounter': () => {
        if (!townView) return item.TownCounter || '';
        if (isCaseSettled(item)) {
          const settlementAv = Number(
            item.SettlementValue ?? item.SCARDeterminationValue ?? item.BARDeterminationValue ?? 0
          );
          if (!Number.isFinite(settlementAv) || settlementAv <= 0) return '';
          const conversionFactor = getConversionFactor(item.EQ, item.RAR);
          return conversionFactor > 0
            ? Math.round(settlementAv / conversionFactor / 10) * 10
            : Math.round(settlementAv);
        }
        if (blankUnsettledTownFields) return '';

        const negotiations = item?.Negotiations;
        const { townRound, latestRound } = getLatestRoundInfo(negotiations);
        if (townRound == null || townRound !== latestRound) return '';

        const offerAv = Number(getMostRecentOfferValue(negotiations, 'Town'));
        if (!Number.isFinite(offerAv)) return '';
        const conversionFactor = getConversionFactor(item.EQ, item.RAR);
        return conversionFactor > 0 ? Math.round(offerAv / conversionFactor / 10) * 10 : Math.round(offerAv);
      },
      'PriorTownOffer': () => {
        if (!townView) return '';
        const negotiations = item?.Negotiations;
        if (!Array.isArray(negotiations) || negotiations.length === 0) return '';
        const { priorRound } = getLatestRoundInfo(negotiations);
        if (priorRound == null || priorRound <= 0) return '';
        const priorRoundObj = negotiations.find((r) => Number(r?.round) === priorRound);
        const townOffer = priorRoundObj?.offers?.find((o) => o?.OfferedBy === 'Town' && o?.Offer != null && o.Offer !== '');
        if (!townOffer) return '';
        const v = Number(townOffer.Offer);
        if (!Number.isFinite(v)) return '';
        return Math.round(v);
      },
      'TownNotes': () => {
        if (townView && blankUnsettledTownFields && !isCaseSettled(item)) return '';
        // For town view, use current round town comment from negotiations, fallback to TownNotes
        if (townView && item.Negotiations && Array.isArray(item.Negotiations)) {
          const currentRoundTownComment = getCurrentRoundCommentForParty(item.Negotiations, 'Town');
          return currentRoundTownComment || item.TownNotes || '';
        }
        return item.TownNotes || '';
      },
      'PctReduction': () => '', // Will be handled by formula
      'parcel_id': () => item.parcel_id || '',
      'Year': () => taxYearToExcelNumber(negotiationObj?.TaxYear),
      'AventineNote': () => {
        // Use current round aventine comment from negotiations for both views
        if (item.Negotiations && Array.isArray(item.Negotiations)) {
          const currentRoundAventineComment = getCurrentRoundCommentForParty(item.Negotiations, 'Aventine');
          return currentRoundAventineComment || item.AventineNote || '';
        }
        return item.AventineNote !== undefined ? item.AventineNote : '';
      },
      // this really shouldn't be relevant since we never upload to s3 if not town view.
      'InternalNote': () => {
        // For internal view only, show the old CaseNotes data
        return !townView && item.CaseNotes !== undefined ? item.CaseNotes : '';
      },
      'Status': () => {
        // Use same logic as town view for all downloads
        return isCaseSettled(item) ? 'Settled' : 'Open';
      },
      'CaseType': () => {
        // Return "Town" if villageRun is 0, "Village" if villageRun is 1
        const villageRun = negotiationObj?.villageRun ?? 0;
        return villageRun === 1 ? 'Village' : 'Town';
      },
      'Workup': () => {
        if (townView && requireLiveOffer && missingLiveOfferForTownExport(item)) return '';
        if (townView && isWorkupAboveIfmv(item)) return '';
        if (item.WorkupLink) return item.WorkupLink;
        const files = Array.isArray(item.S3Files) ? item.S3Files : [];
        const reportTypeForFile =
          negotiationObj?.villageRun === 1 ? 'village' : (negotiationObj?.reportType || 'standard');
        const file = workupReportTypeForXls
          ? findWorkupFileByWorkupReportId(files, reportTypeForFile, workupReportTypeForXls)
          : findWorkupFile(files, reportTypeForFile === 'assessment' ? 'assessment' : 'sales');
        return file?.Url || file?.url || file?.Key || file?.Filename || file?.filename || '';
      },
    };

    // Add round column mappers
    roundColumnKeys.forEach(roundKey => {
      fieldMappers[roundKey] = () => {
        // Use the pre-generated round columns for this item
        const roundColumns = itemRoundColumnsMap.get(index);
        if (roundColumns) {
          const roundColumn = roundColumns.find(round => round.key === roundKey);
          return roundColumn ? roundColumn.value : '';
        }
        return '';
      };
    });

    finalExportKeys.forEach((key) => {
      const match = /^TownRound(\d+)Comment$/.exec(key);
      if (!match) return;
      const roundNumber = Number(match[1]);
      fieldMappers[key] = () => townCommentOnRound(item.Negotiations, roundNumber);
    });

    // Use the helper function to process fields consistently
    return processXlsFields(item, finalExportKeys, fieldMappers, pidToAddressMap, townView, resolveCompAddresses, requireLiveOffer);
  }));

  // Dynamically import ExcelJS only when needed
  const ExcelJS = (await import('exceljs')).default;
  
  // Create workbook and worksheet (mirrors DownloadButton exactly)
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  
  let finalFilteredKeys = finalExportKeys;
  if (finalExportKeys.includes('parcel_id')) {
    if (townView) {
      finalFilteredKeys = finalExportKeys.filter((key) => key !== 'parcel_id').concat(['parcel_id']);
    } else {
      finalFilteredKeys = ['parcel_id'].concat(finalExportKeys.filter((key) => key !== 'parcel_id'));
    }
  }

  // Set up columns using the keys and the clean configuration object
  worksheet.columns = finalFilteredKeys.map(key => {
    const config = columnConfig[key] || {};
    
    // Handle dynamic headers
    let header = config.header || key;

    const townRoundMatch = /^TownRound(\d+)Comment$/.exec(key);
    if (townRoundMatch) {
      return {
        header: `Town Note ${townRoundMatch[1]}`,
        key,
        width: 30,
        style: {
          alignment: { vertical: 'top', wrapText: true },
        },
        hidden: false,
      };
    }

    // Handle round columns
    if (key.startsWith('Round')) {
      const roundNumber = key.replace('Round', '').replace('Comment', '');
      if (key.includes('Comment')) {
        header = `Round ${roundNumber} Comment`;
      } else {
        header = `Round ${roundNumber}`;
      }
      return {
        header: header,
        key: key,
        width: key.includes('Comment') ? 20 : 30,
        style: {
          alignment: {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: false,
          },
        },
        hidden: false,
      };
    }
    
    return {
      header: header,
      key: key,
      width: config.width || undefined,
      style: { numFmt: config.format || undefined },
      hidden: config.hidden || false
    };
  });

  // Style headers for town view input columns (mirrors DownloadButton exactly)
  if (townView) {
    const townResponseColIndexForStyling = finalFilteredKeys.indexOf('TownResponse') + 1;
    const townCounterColIndexForStyling = finalFilteredKeys.indexOf('TownCounter') + 1;
    const townNotesColIndexForStyling = finalFilteredKeys.indexOf('TownNotes') + 1;
    const offerValueColIndexForStyling = finalFilteredKeys.indexOf('OfferValue') + 1;
    
    // Style Town Response header
    if (townResponseColIndexForStyling > 0) {
      const headerCell = worksheet.getCell(1, townResponseColIndexForStyling);
      headerCell.font = { bold: true };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6F3FF' } // Slightly darker blue for headers
      };
      headerCell.border = {
        top: { style: 'thin', color: { argb: 'FF0066CC' } },
        left: { style: 'thin', color: { argb: 'FF0066CC' } },
        bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
        right: { style: 'thin', color: { argb: 'FF0066CC' } }
      };
    }
    
    // Style Town Counter header
    if (townCounterColIndexForStyling > 0) {
      const headerCell = worksheet.getCell(1, townCounterColIndexForStyling);
      headerCell.font = { bold: true };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6F3FF' } // Slightly darker blue for headers
      };
      headerCell.border = {
        top: { style: 'thin', color: { argb: 'FF0066CC' } },
        left: { style: 'thin', color: { argb: 'FF0066CC' } },
        bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
        right: { style: 'thin', color: { argb: 'FF0066CC' } }
      };
    }
    
    // Style Town Notes header
    if (townNotesColIndexForStyling > 0) {
      const headerCell = worksheet.getCell(1, townNotesColIndexForStyling);
      headerCell.font = { bold: true };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6F3FF' } // Slightly darker blue for headers
      };
      headerCell.border = {
        top: { style: 'thin', color: { argb: 'FF0066CC' } },
        left: { style: 'thin', color: { argb: 'FF0066CC' } },
        bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
        right: { style: 'thin', color: { argb: 'FF0066CC' } }
      };
    }
    
    // Style Aventine Offer header
    if (offerValueColIndexForStyling > 0) {
      const headerCell = worksheet.getCell(1, offerValueColIndexForStyling);
      headerCell.font = { bold: true };
    }

    // Style Portfolio Requested AV header
    const portfolioRequestedAVColIndexForStyling = finalFilteredKeys.indexOf('PortfolioRequestedAV') + 1;
    if (portfolioRequestedAVColIndexForStyling > 0) {
      const headerCell = worksheet.getCell(1, portfolioRequestedAVColIndexForStyling);
      headerCell.font = { bold: true };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6F3FF' } // Slightly darker blue for headers
      };
      headerCell.border = {
        top: { style: 'thin', color: { argb: 'FF0066CC' } },
        left: { style: 'thin', color: { argb: 'FF0066CC' } },
        bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
        right: { style: 'thin', color: { argb: 'FF0066CC' } }
      };
    }
  }

  // Add data rows to the worksheet (mirrors DownloadButton exactly)
  reorderedData.forEach((data, rowIndex) => {
    const row = worksheet.addRow(data);
    
    // Make Workup a real clickable hyperlink (ExcelJS: { text, hyperlink })
    const workupColIndex = finalFilteredKeys.indexOf('Workup') + 1;
    if (workupColIndex > 0) {
      const url = data?.Workup;
      if (typeof url === 'string' && url.trim()) {
        const cell = row.getCell(workupColIndex);
        cell.value = { text: workupHyperlinkText(url, data), hyperlink: url };
        cell.font = { color: { argb: 'FF0563C1' }, underline: true };
      }
    }

    // Check if this case is settled for conditional formatting
    const isSettled = data.Status === 'Settled';
    
    // Apply conditional formatting based on settlement status
    if (isSettled) {
      row.eachCell((cell, colNumber) => {
        const columnKey = finalFilteredKeys[colNumber - 1];
        if (columnKey === 'Status') {
          cell.font = { bold: true, color: { argb: 'FF000000' } };
        } else {
          cell.font = { color: { argb: 'FF808080' } };
        }
      });
    } else {
      // Open cases: make Status field bold for visibility
      const statusColIndex = finalFilteredKeys.indexOf('Status') + 1;
      if (statusColIndex > 0) {
        const statusCell = row.getCell(statusColIndex);
        statusCell.font = { bold: true };
      }
    }
    
    // Apply styling and formulas for internal view
    if (!townView) {
      // Style OfferValue column with town view input formatting (only for unsettled cases)
      const offerValueColIndex = finalFilteredKeys.indexOf('OfferValue') + 1;
      if (offerValueColIndex > 0 && !isSettled) {
        const cell = row.getCell(offerValueColIndex);
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' } // Light blue background
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF0066CC' } },
          left: { style: 'thin', color: { argb: 'FF0066CC' } },
          bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
          right: { style: 'thin', color: { argb: 'FF0066CC' } }
        };
      }
      
      // Style town-related fields with same styling as town view (only for unsettled cases)
      const townResponseColIndex = finalFilteredKeys.indexOf('TownResponse') + 1;
      if (townResponseColIndex > 0 && !isSettled) {
        const cell = row.getCell(townResponseColIndex);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' } // Light blue background
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF0066CC' } },
          left: { style: 'thin', color: { argb: 'FF0066CC' } },
          bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
          right: { style: 'thin', color: { argb: 'FF0066CC' } }
        };
      }
      
      const townCounterColIndex = finalFilteredKeys.indexOf('TownCounter') + 1;
      if (townCounterColIndex > 0 && !isSettled) {
        const cell = row.getCell(townCounterColIndex);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' } // Light blue background
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF0066CC' } },
          left: { style: 'thin', color: { argb: 'FF0066CC' } },
          bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
          right: { style: 'thin', color: { argb: 'FF0066CC' } }
        };
      }
      
      const townNotesColIndex = finalFilteredKeys.indexOf('TownNotes') + 1;
      if (townNotesColIndex > 0 && !isSettled) {
        const cell = row.getCell(townNotesColIndex);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' } // Light blue background
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF0066CC' } },
          left: { style: 'thin', color: { argb: 'FF0066CC' } },
          bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
          right: { style: 'thin', color: { argb: 'FF0066CC' } }
        };
      }
      
      const portfolioRequestedAVColIndex = finalFilteredKeys.indexOf('PortfolioRequestedAV') + 1;
      if (portfolioRequestedAVColIndex > 0 && !isSettled) {
        const cell = row.getCell(portfolioRequestedAVColIndex);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' } // Light blue background
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF0066CC' } },
          left: { style: 'thin', color: { argb: 'FF0066CC' } },
          bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
          right: { style: 'thin', color: { argb: 'FF0066CC' } }
        };
      }
      
      // Add formula for PctReduction
      const pctReductionColIndex = finalFilteredKeys.indexOf('PctReduction') + 1;
      const offerValueColIndexForFormula = finalFilteredKeys.indexOf('OfferValue') + 1;
      const ifmvColIndex = finalFilteredKeys.indexOf('IFMV') + 1;
      
      if (pctReductionColIndex > 0 && offerValueColIndexForFormula > 0 && ifmvColIndex > 0) {
        const cell = row.getCell(pctReductionColIndex);
        // Formula: IF(OfferValue="", "", (OfferValue-IFMV)/IFMV)
        cell.value = {
          formula: `=IF(${String.fromCharCode(64 + offerValueColIndexForFormula)}${rowIndex + 2}="","",(${String.fromCharCode(64 + offerValueColIndexForFormula)}${rowIndex + 2}-${String.fromCharCode(64 + ifmvColIndex)}${rowIndex + 2})/${String.fromCharCode(64 + ifmvColIndex)}${rowIndex + 2})`
        };
      }
    }
  });

  // Apply specific formats from columnConfig and add data validation for town view (mirrors DownloadButton exactly)
  worksheet.columns.forEach(column => {
    const config = columnConfig[column.key] || {};
    const format = config.format;
    
    if (format) {
      column.eachCell((cell, rowNumber) => {
        if (rowNumber > 1) { // Skip the header row
          cell.numFmt = format;
        }
      });
    }
    
    // Add data validation for town view
    if (townView) {
      if (column.key === 'TownResponse') {
        // Add dropdown validation for Town Response with visual styling
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            // Get the data for this row to check if it's settled
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            
            cell.protection = { locked: false };
            cell.dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['"Accept,Counter,No Offer,Withdrawal Requested"'],
              showErrorMessage: true,
              errorTitle: 'Invalid Input',
              error: 'Please select from: Accept, Counter, No Offer, or Withdrawal Requested',
              showInputMessage: true,
              promptTitle: 'Town Response',
              prompt: 'Select one of the available options'
            };
            
            // Only add visual styling for unsettled cases
            if (!isSettled) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F8FF' } // Light blue background
              };
              cell.border = {
                top: { style: 'thin', color: { argb: 'FF0066CC' } },
                left: { style: 'thin', color: { argb: 'FF0066CC' } },
                bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
                right: { style: 'thin', color: { argb: 'FF0066CC' } }
              };
            }
          }
        });
      } else if (column.key === 'TownCounter') {
        // Add data validation for Town Counter - numbers only
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            // Get the data for this row to check if it's settled
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            
            cell.protection = { locked: false };
            cell.dataValidation = {
              type: 'decimal',
              allowBlank: true,
              operator: 'greaterThanOrEqual',
              formulae: [0],
              showErrorMessage: true,
              errorTitle: 'Invalid Input',
              error: 'Please enter a valid number.',
            };
            
            // Only add visual styling for unsettled cases
            if (!isSettled) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F8FF' } // Light blue background
              };
              cell.border = {
                top: { style: 'thin', color: { argb: 'FF0066CC' } },
                left: { style: 'thin', color: { argb: 'FF0066CC' } },
                bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
                right: { style: 'thin', color: { argb: 'FF0066CC' } }
              };
            }
          }
        });
      } else if (column.key === 'TownNotes') {
        // Allow editing for Town Notes with visual styling
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            // Get the data for this row to check if it's settled
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            
            // Only add visual styling for unsettled cases
            if (!isSettled) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F8FF' } // Light blue background
              };
              cell.border = {
                top: { style: 'thin', color: { argb: 'FF0066CC' } },
                left: { style: 'thin', color: { argb: 'FF0066CC' } },
                bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
                right: { style: 'thin', color: { argb: 'FF0066CC' } }
              };
            }
          }
        });
      } else if (column.key === 'PortfolioRequestedAV') {
        // Allow editing for Portfolio Requested AV with visual styling
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            // Get the data for this row to check if it's settled
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            
            // Only add visual styling for unsettled cases
            if (!isSettled) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F8FF' } // Light blue background
              };
              cell.border = {
                top: { style: 'thin', color: { argb: 'FF0066CC' } },
                left: { style: 'thin', color: { argb: 'FF0066CC' } },
                bottom: { style: 'thin', color: { argb: 'FF0066CC' } },
                right: { style: 'thin', color: { argb: 'FF0066CC' } }
              };
            }
          }
        });
      } else if (column.key === 'OfferValue') {
        // Style Aventine Offer data cells with bold font (only for unsettled cases)
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            cell.protection = { locked: true };
            // Get the data for this row to check if it's settled
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            
            if (!isSettled) {
              cell.font = { bold: true };
            }
          }
        });
      } else {
        // Lock all other cells
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            cell.protection = { locked: true };
          }
        });
      }
    }
  });

  // Set fixed width or autosize columns based on content length (mirrors DownloadButton exactly)
  worksheet.columns.forEach(column => {
    if (column.key && (String(column.key).startsWith('Round') || /^TownRound\d+Comment$/.test(column.key))) {
      return;
    }
    const fixedWidth = columnConfig[column.key]?.width;
    if (fixedWidth) {
      // Take max between hardcoded width and header length
      const headerLength = column.header ? column.header.length : 0;
      column.width = Math.max(fixedWidth, headerLength + 2);
    } else {
      // Autosize if no fixed width is defined
      const lengths = column.values.map(v => (v ? v.toString().length : 10));
      const maxLength = Math.max(...lengths.filter(v => typeof v === 'number'));
      column.width = maxLength + 2; // Add padding for better readability
    }
  });

  // Apply worksheet protection (mirrors DownloadButton exactly)
  if (isRestricted) {
    // RESTRICTED STATE: Apply the same restrictions as the old town view code
    // First, unlock all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        cell.protection = { locked: false };
      });
    });
    
    // Then re-lock only the non-editable cells (using the old town view restriction logic)
    worksheet.columns.forEach(column => {
      if (column.key === 'OfferValue') {
        // Style Aventine Offer data cells with bold font for unsettled cases only, but keep locked
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            const rowData = reorderedData[rowNumber - 2]; // -2 because rowNumber is 1-indexed and we skip header
            const isSettled = rowData ? rowData.Status === 'Settled' : false;
            cell.protection = { locked: true };
            if (!isSettled) {
              cell.font = { bold: true };
            }
          }
        });
      } else if (!['TownResponse', 'TownCounter', 'TownNotes', 'PortfolioRequestedAV'].includes(column.key)) {
        // Lock all other cells except the input cells
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip the header row
            cell.protection = { locked: true };
          }
        });
      }
      // TownResponse, TownCounter, TownNotes, and PortfolioRequestedAV data cells remain unlocked
    });

    worksheet.getRow(1).eachCell((cell) => {
      cell.protection = { locked: true };
    });

    // Protect the worksheet with settings that allow selecting any cell but only editing unlocked ones
    worksheet.protect('', {
      selectLockedCells: true,  // Allow selecting locked cells
      selectUnlockedCells: true, // Allow selecting unlocked cells
      formatCells: true,        // Allow basic formatting (font, color, etc.)
      formatColumns: false,     // Prevent column formatting
      formatRows: false,        // Prevent row formatting
      insertColumns: false,     // Prevent inserting columns
      insertRows: true,         // Allow inserting rows
      insertHyperlinks: false,  // Prevent inserting hyperlinks
      deleteColumns: false,     // Prevent deleting columns
      deleteRows: true,         // Allow deleting rows
      sort: true,               // Allow sorting/reordering rows
      autoFilter: false,        // Prevent auto-filter
      pivotTables: false        // Prevent pivot tables
    });
  } else {
    // UNLOCKED STATE: Don't apply any protection - let users edit freely
    // No worksheet protection applied
  }

  // Comment wrap: apply once after fills/validation/protection so nothing overwrites it
  finalFilteredKeys.forEach((key, index) => {
    if (key !== 'TownNotes' && !/^TownRound\d+Comment$/.test(key)) return;
    const col = index + 1;
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber < 2) return;
      const cell = row.getCell(col);
      cell.alignment = { ...cell.alignment, wrapText: true, vertical: 'top' };
    });
  });

  // Write to file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  return blob;
};

/** User-facing offers XLS filename (negotiation from persisted store). */
const generateFilename = () => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  logger.log('🔄 [S3 UPLOAD SERVICE] Generating filename:', {
    muniString: negotiationObj?.muniString,
    taxYear: negotiationObj?.TaxYear,
    courtDate: negotiationObj?.CourtDate,
    reportType: negotiationObj?.reportType,
  });

  const muniString = negotiationObj?.muniString || 'Unknown';
  // Always use today's date in mm_dd_yy format and a fixed naming pattern
  const currentDate = new Date();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');
  const year = String(currentDate.getFullYear()).slice(-2);

  return `Aventine_${muniString}_Offers_Sent_${month}_${day}_${year}.xlsx`;
};

/**
 * Upload XLS file to S3 (mirrors DownloadButton exactly)
 * @param {Object} options - Upload options
 * @param {Array} options.caseData - Case data array
 * @param {Array} options.selectedRows - Selected rows (optional)
 * @param {Array} options.keys - Column keys (optional)
 * @param {boolean} options.townView - Town view flag
 * @param {boolean} options.villageMode - Village mode flag
 * @param {boolean} options.avToggleActive - AV toggle active flag
 * @param {string} options.caseType - Case type
 * @param {string} [options.workupReportTypeForXls] - Passed to XLS generation for Workup column file pick
 * @returns {Promise<Object>} Upload result
 */
export const uploadOffersToS3 = async (options) => {
  const {
    caseData,
    selectedRows,
    keys,
    townView = true,
    villageMode = false,
    avToggleActive = false,
    caseType,
    workupReportTypeForXls = null,
  } = options;

  const uploadNegotiationObj = usePersistedDataStore.getState().negotiationObj;

  logger.log('🚀 [S3 UPLOAD SERVICE] Starting S3 upload with ExcelJS:', {
    caseDataLength: caseData?.length,
    selectedRowsLength: selectedRows?.length,
    hasNegotiationObj: !!uploadNegotiationObj,
    townView,
    villageMode,
    avToggleActive,
    caseType,
    reportType: uploadNegotiationObj?.reportType,
    timestamp: new Date().toISOString()
  });

  try {
    // Generate XLS data using ExcelJS (mirrors DownloadButton exactly)
    logger.log('🔄 [S3 UPLOAD SERVICE] Generating XLS data with ExcelJS...');
    const includeCompsInOffersSpreadsheet =
      useOfferOptionsStore.getState().includeCompsInOffersSpreadsheet;

    const blob = await generateXLSData(
      caseData,
      selectedRows,
      keys,
      townView,
      villageMode,
      avToggleActive,
      caseType,
      {
        includeCompsInOffersSheet: includeCompsInOffersSpreadsheet,
        isRestricted: true,
        workupReportTypeForXls,
        throwOnMissingLiveOffer: false,
      }
    );
    logger.log('✅ [S3 UPLOAD SERVICE] XLS data generated:', {
      blobSize: blob.size,
      blobType: blob.type
    });

    // S3 key: timestamped to avoid overwrites (same convention as emailFactory workups)
    const timestamp = Date.now();
    const s3FileName = `Offersheets/offers_${timestamp}.xlsx`;

    // User-facing download name (Content-Disposition) - human-readable, no overwrite concern
    const userFacingFileName = generateFilename();
    logger.log('✅ [S3 UPLOAD SERVICE] S3 key (timestamped):', s3FileName, '| download name:', userFacingFileName);

    // Upload to S3
    logger.log('🔄 [S3 UPLOAD SERVICE] Uploading to S3...');
    logger.log('📁 [S3 UPLOAD SERVICE] S3 file details:', {
      s3FileName,
      userFacingFileName,
      blobSize: blob.size,
      blobType: blob.type
    });
    
    // Upload with user-facing filename for download (presigned URL will use this in Content-Disposition)
    const s3Url = await uploadFileToS3Auto(blob, s3FileName, 'negotiation-files', userFacingFileName);
    
    logger.log('✅ [S3 UPLOAD SERVICE] S3 upload successful!');
    logger.log('🔗 [S3 UPLOAD SERVICE] S3 URL:', s3Url);
    logger.log('📊 [S3 UPLOAD SERVICE] Upload details:', {
      s3Url,
      s3FileName,
      userFacingFileName,
      blobSize: blob.size,
      timestamp: new Date().toISOString()
    });

    return {
      success: true,
      downloadUrl: s3Url,
      csvFilename: userFacingFileName, // User-facing filename without _Assessment
      s3FileName, // Full S3 path
      blobSize: blob.size
    };

  } catch (error) {
    logger.error('❌ [S3 UPLOAD SERVICE] S3 upload failed:', error);
    return {
      success: false,
      error: error.message,
      details: error
    };
  }
};

// Note: getTownViewXLSKeys is already imported and exported from '../config/columns'
// No need to re-export it here